import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, AlertTriangle } from 'lucide-react';
import { authFetch } from '../api';
import Logo from '../components/common/Logo';
import { publicUrl } from '../utils/routing';
import { track } from '../utils/analytics';
import {
  extractOnboardingTemplateRefFromRedirect,
  resolvePostAuthDestination,
  sanitizeOnboardingTemplateRef,
  writeOnboardingState,
} from '../utils/onboarding';

const Login = () => {
  const [searchParams] = useSearchParams();
  const [view, setView] = useState('signin');
  const [formLoading, setFormLoading] = useState(false);
  const [error, setError] = useState('');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');

  const handleAuthSuccess = (data, mode) => {
    if (data?.user?.id) {
      localStorage.setItem('user_id', data.user.id);
      const redirectParam = searchParams.get('redirect');
      const destinationPath = resolvePostAuthDestination({ mode, redirectParam });
      const destination = destinationPath.startsWith('/novo-laudo')
        ? 'report_editor'
        : destinationPath === '/planos'
          ? 'plans'
          : destinationPath.startsWith('/comece')
            ? 'onboarding'
            : 'dashboard';

      if (mode === 'signup') {
        const templateRef = sanitizeOnboardingTemplateRef(searchParams.get('template'))
          || extractOnboardingTemplateRefFromRedirect(redirectParam);
        writeOnboardingState(localStorage, { status: 'pending', templateRef }, data.user.id);
        track('signup_completed', { auth_method: 'email', destination });
        track('account_created', { auth_method: 'email', destination });
        track('onboarding_started', { source: templateRef ? 'public_template' : 'signup' });
        track('trial_started', { plan_tier: 'rotina', trial_days: 7 });
      } else {
        track('login_completed', { auth_method: 'email', destination });
      }

      window.location.href = destinationPath;
    }
  };

  const handleEmailSignIn = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setError('');
    track('login_submitted', { auth_method: 'email' });
    const res = await authFetch('sign-in/email', {
      method: 'POST',
      body: JSON.stringify({ email, password })
    });

    if (res?.status === 'success') {
      handleAuthSuccess(res.data, 'signin');
    } else {
      track('auth_failed', { auth_mode: 'signin', auth_method: 'email' });
      setError(res?.error || 'Não foi possível entrar. Confira o e-mail e a senha e tente novamente.');
      setFormLoading(false);
    }
  };

  const handleEmailSignUp = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setError('');
    track('signup_submitted', { auth_method: 'email', trial_plan: 'rotina' });
    const res = await authFetch('sign-up/email', {
      method: 'POST',
      body: JSON.stringify({ name, email, password })
    });

    if (res?.status === 'success') {
      handleAuthSuccess(res.data, 'signup');
    } else {
      track('auth_failed', { auth_mode: 'signup', auth_method: 'email' });
      setError(res?.error || 'Não foi possível criar a conta. Confira os dados informados e tente novamente.');
      setFormLoading(false);
    }
  };

  const toggleView = () => {
    const next = view === 'signin' ? 'signup' : 'signin';
    setView(next);
    track(next === 'signup' ? 'signup_form_viewed' : 'login_form_viewed', { source: 'auth_toggle' });
  };

  return (
    <div className="min-h-screen bg-paper-alt flex items-center justify-center p-4">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-md w-full">
        <div className="flex flex-col items-center text-center mb-8">
          <a href={publicUrl('/')} aria-label="Laudando Online, página inicial"><Logo markClassName="h-11 w-11" /></a>
          <p className="text-sm text-muted mt-4">{view === 'signin' ? 'Acesse sua conta profissional' : 'Comece seus 7 dias no plano Rotina'}</p>
        </div>

        <div className="bg-paper rounded-card border border-line p-8 shadow-card">
          {error && <div className="mb-6 p-4 bg-danger-tint border border-danger/25 rounded-card flex items-start gap-3 text-danger text-sm"><AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" /><span>{error}</span></div>}

          <form onSubmit={view === 'signin' ? handleEmailSignIn : handleEmailSignUp} className="space-y-5">
            {view === 'signup' && <div><label htmlFor="login-name" className="text-[11px] font-semibold text-muted uppercase tracking-widest mb-1.5 block">Nome completo</label><input id="login-name" type="text" required autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} className="input-field" /></div>}

            <div><label htmlFor="login-email" className="text-[11px] font-semibold text-muted uppercase tracking-widest mb-1.5 block">E-mail</label><input id="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="input-field" placeholder="seu@email.com" /></div>

            <div><label htmlFor="login-password" className="text-[11px] font-semibold text-muted uppercase tracking-widest mb-1.5 block">Senha</label><input id="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="input-field" /></div>

            {view === 'signup' && <p className="text-xs text-muted">Não pedimos cartão para começar.</p>}

            <button type="submit" disabled={formLoading} className={`${view === 'signin' ? 'btn-primary' : 'btn-convert'} w-full`}>
              {formLoading ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" role="status" aria-label="Carregando" /> : <>{view === 'signin' ? 'Entrar' : 'Começar teste de 7 dias'}<ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>

          <div className="mt-8 text-center pt-6 border-t border-line"><button onClick={toggleView} className="text-sm font-semibold text-muted hover:text-navy transition-colors min-h-[44px]">{view === 'signin' ? 'Não tem uma conta? Cadastre-se' : 'Já tem uma conta? Entre agora'}</button></div>
        </div>

      </motion.div>
    </div>
  );
};

export default Login;
